import React, { useEffect, useRef, useState } from 'react';
import './Services.css';
import { 
  FaWrench, 
  FaShower, 
  FaToilet, 
  FaTint, 
  FaFire, 
  FaTools,
  FaExclamationTriangle,
  FaCheckCircle,
  FaArrowRight
} from 'react-icons/fa';

const Services = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      icon: <FaExclamationTriangle />,
      title: 'Dépannage d\'Urgence',
      description: 'Intervention rapide 7j/7 pour toutes vos urgences : fuites, canalisations bouchées, pannes de chauffe-eau.',
      price: 'Dès 80€',
      features: ['Intervention sous 1h', 'Disponible 24/7', 'Devis immédiat'],
      color: '#FF6B6B'
    },
    {
      icon: <FaTint />,
      title: 'Détection de Fuites',
      description: 'Recherche et réparation de fuites d\'eau avec matériel professionnel de pointe et caméra thermique.',
      price: 'Sur devis',
      features: ['Caméra thermique', 'Détection précise', 'Réparation rapide'],
      color: '#4ECDC4'
    },
    {
      icon: <FaShower />,
      title: 'Installation Sanitaire',
      description: 'Pose et remplacement de tous équipements : douche italienne, baignoire, lavabo, robinetterie design.',
      price: 'Sur devis',
      features: ['Équipements haut de gamme', 'Pose professionnelle', 'Garantie décennale'],
      color: '#45B7D1'
    },
    {
      icon: <FaToilet />,
      title: 'WC & Évacuations',
      description: 'Installation et réparation de WC, débouchage de canalisations et curage professionnel.',
      price: 'Dès 90€',
      features: ['WC suspendus', 'Débouchage express', 'Curage canalisation'],
      color: '#96CEB4'
    },
    {
      icon: <FaFire />,
      title: 'Chauffe-eau',
      description: 'Installation, entretien et dépannage de chauffe-eau électrique, gaz ou thermodynamique.',
      price: 'Sur devis',
      features: ['Tous types', 'Entretien régulier', 'Réparation rapide'],
      color: '#FFEAA7'
    },
    {
      icon: <FaTools />,
      title: 'Rénovation Complète',
      description: 'Rénovation totale de salle de bain et cuisine avec design moderne et garantie décennale.',
      price: 'Sur devis',
      features: ['Design personnalisé', 'Travaux clés en main', 'Suivi de chantier'],
      color: '#DFE6E9'
    }
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="section services" ref={sectionRef}>
      <div className="services-bg-animation">
        <div className="service-shape shape-1"></div>
        <div className="service-shape shape-2"></div>
        <div className="service-shape shape-3"></div>
      </div>

      <div className="container">
        <div className="section-title">
          <h2>Nos Services</h2>
          <p>Une gamme complète de prestations de plomberie pour tous vos besoins</p>
        </div>

        <div className="services-grid">
          {services.map((service, index) => (
            <div 
              key={index} 
              className={`service-card ${isVisible ? 'reveal active' : 'reveal'} ${activeCard === index ? 'expanded' : ''}`}
              style={{ 
                animationDelay: `${index * 0.1}s`,
                '--card-color': service.color
              }}
              onMouseEnter={() => setActiveCard(index)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div className="service-card-inner">
                {/* Front */}
                <div className="service-card-front">
                  <div className="service-icon-wrapper">
                    <div className="service-icon">{service.icon}</div>
                    <div className="icon-bg-circle"></div>
                  </div>
                  <h3>{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <div className="service-price-tag">
                    <span className="price-label">Tarif</span>
                    <span className="price-value">{service.price}</span>
                  </div>
                  <div className="service-hover-indicator">
                    <FaArrowRight />
                    <span>Voir les détails</span>
                  </div>
                </div>

                {/* Back (Features on Hover) */}
                <div className="service-card-back">
                  <div className="service-features-list">
                    <h4>Inclus dans ce service :</h4>
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="feature-item">
                        <FaCheckCircle className="feature-check" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button className="service-cta-btn" onClick={scrollToContact}>
                    Demander un devis
                    <FaArrowRight />
                  </button>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="service-card-glow"></div>
              <div className="service-corner-accent"></div>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className={`services-trust ${isVisible ? 'reveal active' : 'reveal'}`}>
          <div className="trust-item">
            <FaCheckCircle className="trust-icon" />
            <span>Devis Gratuit & Sans Engagement</span>
          </div>
          <div className="trust-item">
            <FaCheckCircle className="trust-icon" />
            <span>Garantie Décennale Tous Travaux</span>
          </div>
          <div className="trust-item">
            <FaCheckCircle className="trust-icon" />
            <span>Intervention Rapide 7j/7</span>
          </div>
          <div className="trust-item">
            <FaCheckCircle className="trust-icon" />
            <span>Tarifs Transparents</span>
          </div>
        </div>

        {/* CTA Section */}
        <div className={`services-cta-section ${isVisible ? 'reveal active' : 'reveal'}`}>
          <div className="cta-content">
            <h3>Besoin d'un devis personnalisé ?</h3>
            <p>Contactez-nous maintenant pour une estimation gratuite et sans engagement</p>
          </div>
          <button onClick={scrollToContact} className="btn btn-primary btn-large">
            <FaTools /> Obtenir mon Devis Gratuit
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
